import { useEffect, useMemo, useState } from "react"
import { Head, Link, router, useForm, usePoll } from "@inertiajs/react"
import { AlertTriangle, ArrowLeft, Clock, FileText, Globe, Loader2, RotateCcw, Sparkles, Trash2, Upload, User, Video } from "lucide-react"
import { CoachLayout } from "../components/CoachLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { formatDate } from "./components/bookStatus"

const TABS = [
  { key: "meeting", label: "From my Meetings", icon: Video },
  { key: "article", label: "From my Articles", icon: FileText },
  { key: "upload", label: "Upload a file", icon: Upload },
  { key: "website", label: "Leader's website", icon: Globe },
]
const SOURCE_ICONS = { meeting: Video, article: FileText, upload: Upload, website: Globe }

// Add chapter (no chapter yet) and the chapter's details and sources. A new
// chapter is saved with the leader's details first; its sources are added on
// the next screen, each one read in the background.
export default function ChapterForm({
  currentUser, book, chapter, chapterNumber, sources = [], meetings = [], articles = [], maxSourceWords, shortSourceWords, relationshipNoteMax,
}) {
  const isNew = !chapter
  const form = useForm({
    leader_name: chapter?.leaderName || "",
    leader_role: chapter?.leaderRole || "",
    leader_company: chapter?.leaderCompany || "",
    leader_city: chapter?.leaderCity || "",
    life_verse_text: chapter?.lifeVerseText || "",
    life_verse_reference: chapter?.lifeVerseReference || "",
    relationship_note: chapter?.relationshipNote || "",
    leader_consent: chapter?.leaderConsent || false,
    headshot: null,
  })
  const [preview, setPreview] = useState(chapter?.headshotUrl || null)

  const reading = sources.some((source) => source.reading)
  const { start, stop } = usePoll(5000, { only: ["sources", "chapter"] }, { autoStart: false })
  useEffect(() => {
    if (reading) start()
    else stop()
    return () => stop()
  }, [reading])

  const base = `/app/coach/books/${book.id}/chapters`
  const save = (event) => {
    event.preventDefault()
    form.transform((data) => ({ ...(isNew ? {} : { _method: "patch" }), chapter: data }))
    form.post(isNew ? base : `${base}/${chapter.id}`, { forceFormData: true, preserveScroll: true })
  }

  const pickPhoto = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    form.setData("headshot", file)
    setPreview(URL.createObjectURL(file))
  }

  const sourceWords = sources.reduce((sum, source) => sum + (source.words || 0), 0)

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title={`${isNew ? "Add chapter" : chapter.leaderName} - ${book.title}`} />
      <div className="space-y-6">
        <div className="flex items-start gap-3">
          <Link href={`/app/coach/books/${book.id}`} className="rounded-md p-2 hover:bg-zinc-100" aria-label="Back to the book">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-kf-red">
              Chapter {chapterNumber} of {book.chapterRange[1]} · {book.title}
            </div>
            <h1 className="text-2xl font-bold text-zinc-900">{isNew ? "Add chapter" : chapter.leaderName}</h1>
            <p className="text-zinc-600">
              Tell us who the leader is. Then add the interview and anything else about them. The AI writes the chapter in the Kingdom Factor voice and template.
            </p>
          </div>
        </div>

        {chapter?.missingInfo?.length > 0 && (
          <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div><b>Before the AI can draft, add:</b> {chapter.missingInfo.join(", ")}.</div>
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><User className="h-4 w-4" /> Profiled leader</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
                <Field label="Full name" required error={form.errors.leader_name} className="sm:col-span-2">
                  <Input value={form.data.leader_name} onChange={(e) => form.setData("leader_name", e.target.value)} placeholder="For example: Rachel Osei" required />
                </Field>
                <Field label="Title or role" error={form.errors.leader_role}>
                  <Input value={form.data.leader_role} onChange={(e) => form.setData("leader_role", e.target.value)} placeholder="Founder and CEO" />
                </Field>
                <Field label="Company (optional)" error={form.errors.leader_company}>
                  <Input value={form.data.leader_company} onChange={(e) => form.setData("leader_company", e.target.value)} placeholder="Osei Water Systems" />
                </Field>
                <Field label="City" error={form.errors.leader_city}>
                  <Input value={form.data.leader_city} onChange={(e) => form.setData("leader_city", e.target.value)} placeholder="Indianapolis, Indiana" />
                </Field>
                <Field label="Headshot" error={form.errors.headshot}>
                  <label className="flex cursor-pointer items-center gap-3">
                    <span className="flex h-14 w-12 items-center justify-center overflow-hidden rounded bg-zinc-100">
                      {preview ? <img src={preview} alt="" className="h-full w-full object-cover" /> : <User className="h-5 w-5 text-zinc-400" />}
                    </span>
                    <span className="text-sm">
                      <b>{preview ? "Change photo" : "Upload photo"}</b>
                      <span className="block text-xs text-zinc-500">JPG or PNG. Portrait shape prints best.</span>
                    </span>
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={pickPhoto} />
                  </label>
                </Field>
                <Field label="Life Verse" required error={form.errors.life_verse_text} className="sm:col-span-2"
                  help="Ask the leader for it. The AI never makes up a Life Verse.">
                  <Textarea value={form.data.life_verse_text} onChange={(e) => form.setData("life_verse_text", e.target.value)} rows={3}
                    placeholder="Therefore everyone who hears these words of mine and puts them into practice is like a wise man who built his house on the rock." />
                </Field>
                <Field label="Reference and translation" required error={form.errors.life_verse_reference}>
                  <Input value={form.data.life_verse_reference} onChange={(e) => form.setData("life_verse_reference", e.target.value)} placeholder="Matthew 7:24 · NIV" />
                </Field>
                <Field label="How you know this leader (optional)" error={form.errors.relationship_note} className="sm:col-span-2"
                  help="The AI writes your Author's Notes from this and the interview only. It never says you did something you did not tell it.">
                  <Textarea value={form.data.relationship_note} onChange={(e) => form.setData("relationship_note", e.target.value)} rows={3}
                    maxLength={relationshipNoteMax} placeholder="We have been in the same peer group since 2019. I first met her at..." />
                </Field>
                <label className="flex items-start gap-2 rounded-md border p-3 text-sm sm:col-span-2">
                  <input type="checkbox" className="mt-0.5" checked={form.data.leader_consent} onChange={(e) => form.setData("leader_consent", e.target.checked)} />
                  <span>
                    <b>This leader agreed to be in my book.</b>
                    <span className="block text-zinc-500">Check this only when the leader said yes to a printed profile.</span>
                  </span>
                </label>
                <div className="sm:col-span-2">
                  <Button type="submit" disabled={form.processing}>
                    {isNew ? "Save and add sources" : "Save details"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {isNew ? (
            <Card className="border-dashed">
              <CardContent className="flex h-full flex-col items-center justify-center py-12 text-center text-sm text-zinc-500">
                <Video className="mb-2 h-6 w-6" />
                Save the leader's details first. Then add the interview and other sources here.
              </CardContent>
            </Card>
          ) : (
            <SourcesCard
              book={book} chapter={chapter} sources={sources} meetings={meetings} articles={articles}
              sourceWords={sourceWords} maxSourceWords={maxSourceWords} shortSourceWords={shortSourceWords}
            />
          )}
        </div>
      </div>
    </CoachLayout>
  )
}

function Field({ label, required, error, help, className, children }) {
  return (
    <div className={`space-y-1.5 ${className || ""}`}>
      <Label>{label}{required && <span className="text-kf-red"> *</span>}</Label>
      {children}
      {help && <p className="text-xs text-zinc-500">{help}</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  )
}

function SourcesCard({ book, chapter, sources, meetings, articles, sourceWords, maxSourceWords, shortSourceWords }) {
  const [tab, setTab] = useState("meeting")
  const [url, setUrl] = useState("")
  const [query, setQuery] = useState("")
  const base = `/app/coach/books/${book.id}/chapters/${chapter.id}`
  const chosen = useMemo(() => new Set(sources.map((s) => `${s.kind}:${s.label}`)), [sources])

  const add = (source, options = {}) => router.post(`${base}/sources`, { source }, { preserveScroll: true, ...options })
  const filtered = (list) => list.filter((item) => item.title.toLowerCase().includes(query.toLowerCase()))

  const canDraft = chapter.missingInfo.length === 0 && !chapter.generating
  return (
    <Card>
      <CardHeader><CardTitle className="text-lg">Chapter sources</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-1 border-b" role="tablist">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
              className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm ${tab === key ? "border-kf-red font-medium text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        {(tab === "meeting" || tab === "article") && (
          <div className="space-y-2">
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search your ${tab === "meeting" ? "Meetings" : "Articles"}`} />
            <div className="max-h-64 divide-y overflow-y-auto rounded-md border">
              {filtered(tab === "meeting" ? meetings : articles).map((item) => (
                <div key={item.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{item.title}</div>
                    <div className="text-xs text-zinc-500">
                      {tab === "meeting" ? `${formatDate(item.date)} · ${item.words.toLocaleString()} words` : `${item.status} · ${formatDate(item.updatedAt)}`}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" disabled={chosen.has(`${tab}:${item.title}`)}
                    onClick={() => add({ kind: tab, [`${tab}_id`]: item.id })}>
                    {chosen.has(`${tab}:${item.title}`) ? "Added" : "Add"}
                  </Button>
                </div>
              ))}
              {filtered(tab === "meeting" ? meetings : articles).length === 0 && (
                <div className="px-3 py-6 text-center text-sm text-zinc-500">
                  {tab === "meeting" ? "No meetings with a transcript yet." : "No articles yet."}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "upload" && (
          <label className="flex cursor-pointer flex-col items-center gap-1 rounded-md border border-dashed p-6 text-center text-sm">
            <Upload className="h-5 w-5 text-zinc-500" />
            <b>Choose a file</b>
            <span className="text-xs text-zinc-500">PDF, Word (.docx), text, or a .vtt transcript (Read AI, Zoom). Up to 50 MB.</span>
            <input type="file" className="sr-only" accept=".pdf,.docx,.txt,.md,.vtt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,text/vtt"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) add({ kind: "upload", file }, { forceFormData: true })
                e.target.value = ""
              }} />
          </label>
        )}

        {tab === "website" && (
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); add({ kind: "website", url }, { onSuccess: () => setUrl("") }) }}>
            <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://leadercompany.com" required />
            <Button type="submit" variant="outline">Add</Button>
          </form>
        )}
        {tab === "website" && <p className="text-xs text-zinc-500">We read the page and up to 2 more pages on the same site, such as About or Our story.</p>}

        <div className="space-y-2 border-t pt-4">
          <div className="flex justify-between text-sm">
            <b>Selected sources</b>
            <span className={sourceWords > maxSourceWords ? "text-red-600" : "text-zinc-500"}>
              {sourceWords.toLocaleString()} of {maxSourceWords.toLocaleString()} words
            </span>
          </div>
          {sources.length === 0 && <p className="text-sm text-zinc-500">No sources yet. Add the leader's interview first.</p>}
          {sources.map((source) => {
            const Icon = SOURCE_ICONS[source.kind]
            return (
              <div key={source.id} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                <Icon className="h-4 w-4 shrink-0 text-zinc-500" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{source.label}</div>
                  <div className="text-xs">
                    {source.reading && <span className="inline-flex items-center gap-1 text-zinc-500"><Loader2 className="h-3 w-3 animate-spin" /> Reading…</span>}
                    {source.error && <span className="text-red-600">{source.error}</span>}
                    {!source.reading && !source.error && <span className="text-zinc-500">{source.words.toLocaleString()} words</span>}
                  </div>
                </div>
                {source.error && (
                  <Button size="icon-sm" variant="ghost" aria-label="Try again" onClick={() => router.post(`${base}/sources/${source.id}/retry`, {}, { preserveScroll: true })}>
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
                <Button size="icon-sm" variant="ghost" aria-label="Remove source" onClick={() => router.delete(`${base}/sources/${source.id}`, { preserveScroll: true })}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )
          })}
        </div>

        {!chapter.hasDraft && sourceWords > 0 && sourceWords < shortSourceWords && (
          <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <b>Your sources are short ({sourceWords.toLocaleString()} words).</b> The AI uses only what the sources say, so the chapter will be short too. That is fine if it fits your book. For a full chapter, add the interview transcript.
            </span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 border-t pt-4">
          {chapter.hasDraft ? (
            <Button asChild><Link href={base}>Back to the chapter</Link></Button>
          ) : (
            <Button disabled={!canDraft} onClick={() => router.post(`${base}/draft`)}>
              <Sparkles className="h-4 w-4" /> Draft this chapter
            </Button>
          )}
          <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
            <Clock className="h-3.5 w-3.5" />
            {chapter.hasDraft ? "A new source does not change the draft. Use Redraft on the chapter page." : "Takes about 3 to 5 minutes. You can leave this page; we email you."}
          </span>
        </div>
      </CardContent>
    </Card>
  )
}
